// Geometria de charts: datos + dimensiones -> primitivas dibujables.
//
// FUNCION PURA. Cero React, cero DOM, cero SVG. La razon es el PDF:
// @react-pdf/renderer tiene sus propias primitivas (<Svg>, <Rect>, ...) y NO
// renderiza DOM. Con la geometria separada, el chart de pantalla y el del PDF
// consumen la MISMA salida y son identicos por construccion — en vez de
// rasterizar el DOM a PNG (borroso al imprimir) o mantener dos layouts que
// derivan.
//
// Testeable sin renderizar nada: ver geometry.test.ts.

export interface StackSegment {
  /** Clave de serie, para mapear a color y leyenda. */
  key: string;
  value: number;
}

export interface StackedBarInput {
  label: string;
  segments: StackSegment[];
}

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
  key: string;
  value: number;
  /** Indice de la barra: lo usa el hover para resaltar el grupo. */
  barIndex: number;
}

export interface AxisTick {
  value: number;
  y: number;
  label: string;
}

export interface BarLabel {
  x: number;
  y: number;
  text: string;
  barIndex: number;
}

export interface StackedBarGeometry {
  width: number;
  height: number;
  plot: { x: number; y: number; w: number; h: number };
  rects: Rect[];
  ticks: AxisTick[];
  xLabels: BarLabel[];
  /** Total por barra, para el label directo arriba de cada una. */
  totals: BarLabel[];
  /** Máximo del dominio. 0 si no hay datos. */
  domainMax: number;
}

export interface StackedBarOptions {
  width: number;
  height: number;
  padding?: { top: number; right: number; bottom: number; left: number };
  /** Formatea el valor del eje Y y los labels de total. */
  formatValue?: (v: number) => string;
  /** Cantidad de ticks del eje Y (aprox). */
  tickCount?: number;
  /** Gap en px entre segmentos apilados (el spacer de superficie). */
  segmentGap?: number;
  maxBarWidth?: number;
}

const DEFAULT_PADDING = { top: 24, right: 12, bottom: 28, left: 56 };

/**
 * Escala "linda": paso de 1/2/5 x 10^n y maximo redondeado a un multiplo de
 * ese paso.
 *
 * Devuelve el PASO ademas del maximo, y no solo el maximo, porque los ticks
 * tienen que caer en multiplos del paso. Calcular un paso lindo y despues
 * emitir ticks en domainMax/tickCount es el bug obvio: con 12100 daria un
 * maximo de 15000 (lindo) pero ticks en 3750 / 7500 / 11250 (ilegibles).
 */
export function niceScale(raw: number, tickCount: number): { max: number; step: number } {
  if (raw <= 0) return { max: 0, step: 0 };
  const rough = raw / Math.max(1, tickCount);
  const mag = Math.pow(10, Math.floor(Math.log10(rough)));
  const norm = rough / mag;
  const step = (norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10) * mag;
  return { max: Math.ceil(raw / step) * step, step };
}

/** Solo el maximo. Atajo de niceScale para los callers que no dibujan ejes. */
export function niceMax(raw: number, tickCount: number): number {
  return niceScale(raw, tickCount).max;
}

export function buildStackedBars(
  data: StackedBarInput[],
  opts: StackedBarOptions,
): StackedBarGeometry {
  const padding = opts.padding ?? DEFAULT_PADDING;
  const tickCount = opts.tickCount ?? 4;
  const segmentGap = opts.segmentGap ?? 2;
  const fmt = opts.formatValue ?? ((v: number) => String(v));

  const plot = {
    x: padding.left,
    y: padding.top,
    w: Math.max(0, opts.width - padding.left - padding.right),
    h: Math.max(0, opts.height - padding.top - padding.bottom),
  };

  const totalsRaw = data.map((d) => d.segments.reduce((s, x) => s + Math.max(0, x.value), 0));
  const rawMax = totalsRaw.length > 0 ? Math.max(...totalsRaw) : 0;
  const { max: domainMax, step: tickStep } = niceScale(rawMax, tickCount);

  // Dominio vacio (sin datos, o todo en cero): geometria valida y vacia. Es el
  // caso normal antes de que haya cobros, no un error.
  if (domainMax <= 0 || data.length === 0) {
    return {
      width: opts.width,
      height: opts.height,
      plot,
      rects: [],
      ticks: [{ value: 0, y: plot.y + plot.h, label: fmt(0) }],
      xLabels: data.map((d, i) => ({
        x: plot.x + (plot.w / Math.max(1, data.length)) * (i + 0.5),
        y: plot.y + plot.h + 16,
        text: d.label,
        barIndex: i,
      })),
      totals: [],
      domainMax: 0,
    };
  }

  const toY = (v: number): number => plot.y + plot.h - (v / domainMax) * plot.h;
  const slot = plot.w / data.length;
  const barW = Math.min(opts.maxBarWidth ?? 48, slot * 0.62);

  const rects: Rect[] = [];
  const totals: BarLabel[] = [];
  const xLabels: BarLabel[] = [];

  data.forEach((d, i) => {
    const cx = plot.x + slot * (i + 0.5);
    const x = cx - barW / 2;
    let acc = 0;

    d.segments.forEach((seg) => {
      const v = Math.max(0, seg.value);
      if (v <= 0) return;
      const yTop = toY(acc + v);
      const yBottom = toY(acc);
      // El gap entre segmentos sale de la ALTURA, no del ancho: asi la pila
      // sigue sumando el total exacto en el eje. Nunca deja altura negativa.
      const h = Math.max(0, yBottom - yTop - segmentGap);
      rects.push({ x, y: yTop, w: barW, h, key: seg.key, value: v, barIndex: i });
      acc += v;
    });

    if (acc > 0) {
      totals.push({ x: cx, y: toY(acc) - 6, text: fmt(acc), barIndex: i });
    }
    xLabels.push({ x: cx, y: plot.y + plot.h + 16, text: d.label, barIndex: i });
  });

  // Ticks en multiplos del paso lindo, no en domainMax/tickCount. La cantidad
  // puede diferir de tickCount por uno o dos: preferimos numeros redondos en el
  // eje antes que una cantidad exacta de lineas.
  const ticks: AxisTick[] = [];
  for (let v = 0; v <= domainMax + tickStep * 0.001; v += tickStep) {
    const value = Math.round(v * 100) / 100;
    ticks.push({ value, y: toY(value), label: fmt(value) });
  }

  return { width: opts.width, height: opts.height, plot, rects, ticks, xLabels, totals, domainMax };
}
